import {
  initPreferences,
  updatePreferences
} from '../../utils/userPreferences';
import actions from './actions';
import { incidentOperations } from '../incident';
import { mapOperations } from '../map';
import { resourceOperations } from '../resource';
import { getUrlParams } from '../../utils/helpers';
import isEqual from 'lodash/isEqual';

const envs = ['test', 'test-next', 'oat', 'oat-next', 'prod'];

//pull in global config parameters here
const globalEnv = {
  version: "1.9.8",
  directorGroup: "b288e6b7ece74f8da8b25d335f3f5cf8",
  dashboardGroup: "d87bb84373524bc5a753173060bcb69f",
  loginWithPopup: false,
  geometryServiceUrl: "",
  portalUrl: "https://geoplatform.maps.arcgis.com",
  jsapiUrl: "//js.arcgis.com/4.9/",
  jsapiV4: true,
  dataGridOptions: {
    defaultPageSize: 20,
    pollingInterval: null
  },
  layers: {
    INCIDENTS: "Incidents",
    INCIDENTS_HEATMAP: "Incidents (Heatmap)",
    INCIDENT_HISTORY: "Incidents (Historical)",
    RESOURCES: "Resources"
  },
  tables: {
    INCIDENT_RELATIONSHIPS: "Irwin - Incident Relationships",
    INCIDENT_RESOURCES: "Irwin - Incident Resource Summaries",
    RESOURCE_EXPERIENCE: "Resource - Experience",
    RESOURCE_CAPABILITIES: "Resource - Resource Capability",
    RESOURCE_CAPABILITY_TYPES: "Resource - Resource Capability Type",
    RESOURCE_CAPABILITY_TYPE_RELATIONSHIP: "Resource - Capability Type Relationship",
    RESOURCE_RELATIONSHIP: "Resource - Resource Relationship",
    RESOURCE_CAPABILITY_REQUESTS: "Resource - Capability Request"
  },
  formatters: {
    dateFormat: "M/D/YYYY",
    dateTimeFormat: "M/D/YY HH:mm"
  },
  csvQueryManyOptions: {
    clientMaxRecordCount: 10000,
    pageSize: 1000,
    concurrentQueriesCount: 5
  },
  changelog: "/changelog.html",
  feedback: {
    to: ["kara_stringer@ios.doi.gov"],
    subject: "Irwin Observer Feedback",
    body:
      '[Please be as descriptive as possible and include screenshots when applicable]'
  }
}

const setOriginFieldInfos = actions.setOriginFieldInfos;

const fetchConfig = () => dispatch => {
  const env = {...process.env, ...globalEnv}
  const v = getUrlParams().v;

   // no params, just continue
  if (!v) {
    env.env = process.env.REACT_APP_ENVIRONMENT_VAR;
    env.is_next = false;
    dispatch(actions.setConfig(env));
    return;
  }

  // 'next' param, apply to configured env
  if (v === 'next') {
    env.is_next = true;
    env.env = process.env.REACT_APP_ENVIRONMENT_VAR_NEXT;
    dispatch(actions.setConfig(env));
    return;
  }

  // full env identified, set it to config
  if (envs.indexOf(v) !== -1) {
    env.env = v;
    dispatch(actions.setConfig(env));
    return;
  }

  dispatch(actions.setConfig(env));

};

const initUserPrefs = () => async (dispatch, getState) => {
  const user = getState().auth.user;
  const config = getState().config;

  const { id, preferences } = await initPreferences(user, config);

  // need to add in the sql filter objects if they don't exist
  if (preferences && !preferences.sqlFilters) {
    preferences.sqlFilters = {};
  } else if (preferences && !preferences.resourceSqlFilters) {
    preferences.resourceSqlFilters = {};
  }

  dispatch(actions.initUserPrefs(id, preferences));

  dispatch(
    incidentOperations.setInitialIncidentListColumns(
      preferences.incidentListColumns
    )
  );

  dispatch(
    incidentOperations.setInitialIncidentResourceListColumns(
      preferences.incidentResourceListColumns
    )
  );

  dispatch(
    incidentOperations.setInitialIncidentRequestListColumns(
      preferences.incidentRequestListColumns
    )
  );

  dispatch(
    incidentOperations.setInitialIncidentExperienceListColumns(
      preferences.incidentExperienceListColumns
    )
  );

  dispatch(
    resourceOperations.setInitialResourceListColumns(
      preferences.resourceListColumns
    )
  );

  dispatch(
    mapOperations.setMapColumnVisible(
      preferences.mapColumnVisible
    )
  );

};

const addSqlFilter = filterDef => (dispatch, getState) => {
  dispatch(actions.addSqlFilter({ ...filterDef }));
  updatePreferencesItem(getState);
};

const renameSqlFilter = filterRenameInfo => (dispatch, getState) => {
  dispatch(actions.renameSqlFilter(filterRenameInfo));
  updatePreferencesItem(getState);
};

const removeSqlFilter = filterName => (dispatch, getState) => {
  dispatch(actions.removeSqlFilter(filterName));
  updatePreferencesItem(getState);
};

const addResourceSqlFilter = filterDef => (dispatch, getState) => {
  dispatch(actions.addResourceSqlFilter({ ...filterDef }));
  updatePreferencesItem(getState);
};

const renameResourceSqlFilter = filterRenameInfo => (dispatch, getState) => {
  dispatch(actions.renameResourceSqlFilter(filterRenameInfo));
  updatePreferencesItem(getState);
};

const removeResourceSqlFilter = filterName => (dispatch, getState) => {
  dispatch(actions.removeResourceSqlFilter(filterName));
  updatePreferencesItem(getState);
};

const setIncidentListColumns = columns => (dispatch, getState) => {
  // only communicate intent to update user's saved config if there is a difference
  // between incoming columns and what is already stored in the user's preferences
  if (isEqual(columns, getState().config.preferences.incidentListColumns)) {
    return;
  }

  dispatch(actions.setIncidentListColumns(columns));
  updatePreferencesItem(getState);
};


const setMapPrefs = mapColumnVisible => (dispatch, getState) => {
  // only communicate intent to update user's saved config if there is a difference
  // between incoming columns and what is already stored in the user's preferences
  if (isEqual(mapColumnVisible, getState().mapColumnVisible)) {
    return;
  }
  dispatch(actions.setMapPrefs(mapColumnVisible));
  updatePreferencesItem(getState);
};

const setIncidentResourceListColumns = columns => (dispatch, getState) => {
  if (
    isEqual(columns, getState().config.preferences.incidentResourceListColumns)
  ) {
    return;
  }

  dispatch(actions.setIncidentResourceListColumns(columns));
  updatePreferencesItem(getState);
};

const setIncidentRequestListColumns = columns => (dispatch, getState) => {
  if (
    isEqual(columns, getState().config.preferences.incidentRequestListColumns)
  ) {
    return;
  }

  dispatch(actions.setIncidentRequestListColumns(columns));
  updatePreferencesItem(getState);
};

const setIncidentExperienceListColumns = columns => (dispatch, getState) => {
  if (
    isEqual(
      columns,
      getState().config.preferences.incidentExperienceListColumns
    )
  ) {
    return;
  }

  dispatch(actions.setIncidentExperienceListColumns(columns));
  updatePreferencesItem(getState);
};

const setResourceListColumns = columns => (dispatch, getState) => {
  // only communicate intent to update user's saved config if there is a difference
  // between incoming columns and what is already stored in the user's preferences
  if (isEqual(columns, getState().config.preferences.resourceListColumns)) {
    return;
  }

  dispatch(actions.setResourceListColumns(columns));
  updatePreferencesItem(getState);
};

function updatePreferencesItem(getState) {
  const preferencesItemId = getState().config.preferencesItemId;
  const preferences = getState().config.preferences;

  updatePreferences(preferencesItemId, preferences);
}

export default {
  fetchConfig,
  setOriginFieldInfos,
  initUserPrefs,

  addSqlFilter,
  renameSqlFilter,
  removeSqlFilter,
  addResourceSqlFilter,
  renameResourceSqlFilter,
  removeResourceSqlFilter,

  setIncidentListColumns,
  setIncidentResourceListColumns,
  setIncidentRequestListColumns,
  setIncidentExperienceListColumns,
  setResourceListColumns,
  setMapPrefs
};



// WEBPACK FOOTER //
// ./src/redux/config/operations.js